import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { withRouter, Link as RouterLink } from 'react-router-dom';
import moment from 'moment';
import Page from '../../components/Page';
import { NetworkContext } from '../../context/NetworkContext';
import { SnackBarContext } from '../../context/SnackbarContext';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: '100%',
    padding: theme.spacing(3)
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3)
  },
  card: {
    marginTop: theme.spacing(3)
  },
  barCell: {
    minWidth: 160
  },
  announceActions: {
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center'
  }
}));

function displayLocalized(value) {
  if (value == null) {
    return '';
  }
  if (typeof value === 'object') {
    return value.English || value.english || Object.values(value)[0] || '';
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object') {
        return parsed.English || parsed.english || Object.values(parsed)[0] || value;
      }
    } catch (e) {
      return value;
    }
  }
  return String(value);
}

function csvEscape(value) {
  const text = value == null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

export const SurveyResults = withRouter(props => {
  const classes = useStyles();
  const surveyId = props.match?.params?.id || props.location?.pathname?.split('/')?.[2];
  const { sendNetworkRequest } = React.useContext(NetworkContext);
  const snackbarCtx = React.useContext(SnackBarContext);
  const [payload, setPayload] = useState(null);
  const [announceTotal, setAnnounceTotal] = useState('');
  const [announceNotes, setAnnounceNotes] = useState('');
  const [announceCounts, setAnnounceCounts] = useState({});
  const [saving, setSaving] = useState(false);

  function applyAnnouncementForm(result) {
    const announced = result?.announcement;
    const sourceOptions = announced?.options?.length ? announced.options : (result?.options || []);
    const counts = {};
    sourceOptions.forEach(opt => {
      counts[opt.id] = opt.count != null ? String(opt.count) : '0';
    });
    setAnnounceCounts(counts);
    setAnnounceTotal(String(announced?.total != null ? announced.total : (result?.total || 0)));
    setAnnounceNotes(announced?.notes || '');
  }

  async function fetchResults() {
    const result = await sendNetworkRequest('/admin/getsurveyresults', {}, { id: surveyId }, false);
    setPayload(result);
    applyAnnouncementForm(result);
  }

  useEffect(() => {
    if (surveyId) {
      fetchResults();
    }
  }, [surveyId]);

  function downloadCsv() {
    if (!payload) {
      return;
    }
    const lines = [];
    lines.push(['Question', displayLocalized(payload.survey?.question)].map(csvEscape).join(','));
    lines.push(['Total responses', payload.total || 0].map(csvEscape).join(','));
    lines.push('');
    lines.push(['Option', 'Count'].map(csvEscape).join(','));
    (payload.options || []).forEach(opt => {
      lines.push([displayLocalized(opt.option), opt.count || 0].map(csvEscape).join(','));
    });
    lines.push('');
    lines.push(['User', 'Mobile', 'Comment', 'Created At'].map(csvEscape).join(','));
    (payload.comments || []).forEach(row => {
      lines.push([
        row.user_name || '',
        row.mobile || '',
        row.comment || '',
        row.createdAt ? moment(row.createdAt).format('DD MMM YYYY hh:mm a') : ''
      ].map(csvEscape).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `survey-${surveyId}-results.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  async function announceResults() {
    setSaving(true);
    const options = (payload.options || []).map(opt => ({
      id: opt.id,
      count: parseInt(announceCounts[opt.id], 10) || 0,
    }));
    const result = await sendNetworkRequest('/admin/announcesurveyresults', {}, {
      id: surveyId,
      total: parseInt(announceTotal, 10) || 0,
      notes: announceNotes,
      options,
      publish: true,
    }, false);
    setSaving(false);
    snackbarCtx.setSnack({
      ...snackbarCtx,
      open: true,
      type: result?.status === 'success' ? 'success' : 'error',
      msg: result?.message || 'Unable to announce results',
      vertical: 'top',
    });
    if (result?.status === 'success') {
      fetchResults();
    }
  }

  const total = payload?.total || 0;
  const options = payload?.options || [];
  const comments = payload?.comments || [];
  const announcement = payload?.announcement;

  return (
    <Page className={classes.root} title="Survey Results">
      <div className={classes.header}>
        <div>
          <Button component={RouterLink} to="/survey" size="small">
            Back to Survey
          </Button>
          <Typography component="h1" variant="h3">
            Survey Results
          </Typography>
          <Typography variant="body1" style={{ marginTop: 8 }}>
            {displayLocalized(payload?.survey?.question)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Actual responses: {total}
            {announcement?.is_published ? ' · Announced to app users' : ''}
          </Typography>
        </div>
        <Button color="primary" variant="contained" onClick={downloadCsv} disabled={!payload}>
          Download CSV
        </Button>
      </div>

      <Card className={classes.card}>
        <CardHeader title="Actual option counts (read only)" />
        <Divider />
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Option</TableCell>
                <TableCell>Count</TableCell>
                <TableCell>Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {options.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>No options</TableCell>
                </TableRow>
              ) : options.map(opt => {
                const count = opt.count || 0;
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <TableRow key={opt.id}>
                    <TableCell>{displayLocalized(opt.option)}</TableCell>
                    <TableCell>{count}</TableCell>
                    <TableCell className={classes.barCell}>
                      <LinearProgress variant="determinate" value={pct} />
                      <Typography variant="caption">{pct}%</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardHeader
          title="Announce results"
          subheader="These numbers are saved separately. Actual responses are not changed."
        />
        <Divider />
        <CardContent>
          <TextField
            label="Announced total"
            type="number"
            value={announceTotal}
            onChange={event => setAnnounceTotal(event.target.value)}
            variant="outlined"
            margin="dense"
            style={{ maxWidth: 220, marginBottom: 16 }}
          />
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Option</TableCell>
                <TableCell>Announced count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {options.map(opt => (
                <TableRow key={`announce-${opt.id}`}>
                  <TableCell>{displayLocalized(opt.option)}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={announceCounts[opt.id] != null ? announceCounts[opt.id] : '0'}
                      onChange={event => setAnnounceCounts({
                        ...announceCounts,
                        [opt.id]: event.target.value,
                      })}
                      variant="outlined"
                      margin="dense"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TextField
            label="Announcement note (optional)"
            value={announceNotes}
            onChange={event => setAnnounceNotes(event.target.value)}
            variant="outlined"
            fullWidth
            multiline
            rows={3}
            margin="normal"
          />
          <div className={classes.announceActions}>
            <Button
              color="primary"
              variant="contained"
              onClick={announceResults}
              disabled={!payload || saving}
            >
              {announcement?.is_published ? 'Update announcement' : 'Announce to users'}
            </Button>
            {announcement?.announced_at ? (
              <Typography variant="body2" color="textSecondary" style={{ marginLeft: 16 }}>
                Last announced {moment(announcement.announced_at).format('DD MMM YYYY | hh:mm a')}
              </Typography>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardHeader title="Comments" />
        <Divider />
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Comment</TableCell>
                <TableCell>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>No comments</TableCell>
                </TableRow>
              ) : comments.map((row, index) => (
                <TableRow key={`${row.mobile || 'c'}-${index}`}>
                  <TableCell>{row.user_name || '—'}</TableCell>
                  <TableCell>{row.mobile || '—'}</TableCell>
                  <TableCell>{row.comment}</TableCell>
                  <TableCell>
                    {row.createdAt ? moment(row.createdAt).format('DD MMM YYYY | hh:mm a') : ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Page>
  );
});

export default SurveyResults;
